import { __esDecorate, __runInitializers } from "tslib";
import { asType, getType } from '#library/type.library.js';
import { isNumber, isFunction } from '#library/assertion.library.js';
import { ownEntries } from '#library/primitive.library.js';
import { secure, proxify } from '#library/proxy.library.js';
import { Serializable } from '#library/class.library.js';
import { memoizeMethod } from '#library/function.library.js';
/** key to use for identifying Enumify objects */
const tag = 'Enumify';
const ENUM = secure(Object.create(null, {
    keys: memoizeMethod('keys', function () { return ownEntries(this, true).map(([key]) => key); }),
    values: memoizeMethod('values', function () { return ownEntries(this, true).map(([_, val]) => val); }),
    entries: memoizeMethod('entries', function () { return ownEntries(this, true); }),
    invert: memoizeMethod('invert', function () { return Object.fromEntries(this.entries().map(([key, val]) => [val, key])); }),
    has: value(function (key) { return this.keys().includes(key); }),
    count: value(function () { return this.keys().length; }),
    includes: value(function (search) { return this.values().includes(search); }),
    keyOf: value(function (search) { return this.invert()[search]; }),
    extend: value(function (list, frozen) { return enumify.call(this, list, frozen); }),
    forEach: value(function (fn, thisArg) { this.entries().forEach((entry, index) => fn.call(thisArg, entry, index, this)); }),
    filter: value(function (fn, thisArg) { return enumify(this.entries().reduce((acc, entry, index) => (fn.call(thisArg, entry, index, this) ? Object.assign(acc, { [entry[0]]: entry[1] }) : acc), {})); }),
    map: value(function (fn, thisArg) { return enumify(this.entries().reduce((acc, entry, index) => Object.assign(acc, { [entry[0]]: fn.call(thisArg, entry, index, this) }), {})); }),
    [Symbol.iterator]: value(function* () { for (const entry of this.entries())
        yield entry; }),
    [Symbol.toStringTag]: { enumerable: false, configurable: false, writable: false, value: tag }
}));
function value(val) {
    return { enumerable: false, configurable: false, writable: false, value: val };
}
export function enumify(list, frozen = true) {
    const type = getType(this);
    const proto = (type !== 'Module' && isFunction(this?.has)) ? this : ENUM;
    const arg = asType(list);
    const target = Object.create(proto);
    switch (arg.type) {
        case 'Enumify':
        case 'Object':
            Object.assign(target, arg.value);
            break;
        case 'Array':
            arg.value.forEach((key, index) => {
                if (isNumber(key))
                    throw new Error('Enumify: numeric keys are not supported');
                target[key] = index;
            });
            break;
        default:
            throw new Error(`Enumify: invalid argument type: ${arg.type}`);
    }
    return proxify(target, true, frozen); // proxy is ALWAYS frozen (read-only), but target is only 'locked' if requested
}
/** create an entry in the Serialization Registry to describe how to rebuild an Enum */
let Enumify = (() => {
    let _classDecorators = [Serializable];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Enumify = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Enumify = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        constructor(list) {
            return enumify(list);
        }
    };
    return Enumify = _classThis;
})();
export { Enumify };
